import React, { useEffect, useState, useRef } from "react";
import MyEditor from "./AddNewTaskModalEditor";
import {
  FIREBASE_CONVERSATIONS,
  FIREBASE_JOURNAL_THREADS,
} from "../Utils/constant";
import {
  collection,
  query,
  where,
  getDoc,
  getDocs,
  doc,
  onSnapshot,
  orderBy,
  addDoc,
  serverTimestamp,
  runTransaction,
  setDoc,
  limit,
} from "firebase/firestore";
import { db } from "../Utils/firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useGetJournalDetail,
  useGetSharePdfUrl,
} from "../Controllers/Journal/JournalController";
import {
  useCreateMultiGroupPost,
  useGetTribeGroups,
} from "../Controllers/Tribe/TribePostController";
import { filterTribeGroupByAccessLevel } from "../helpers/TribeGroupeHelper";
import useUserStore from "../zustand-stores/UserStore";

const JournalShareModal = ({ show, title, onClose, journalId }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedChats, setSelectedChats] = useState([]);
  const [selectedTribeGroups, setSelectedTribeGroups] = useState([]);
  const [messageContent, setMessageContent] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [selectedTabs, setSelectedTabs] = useState(["full"]);
  const [sharePdfUrl, setSharePdfUrl] = useState("");
  const [urlGenerationError, setUrlGenerationError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [isTribeGroupDropdownOpen, setIsTribeGroupDropdownOpen] =
    useState(false);
  const tribeGroupDropdownRef = useRef(null);
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const chatDropdownRef = useRef(null);
  const [loadingFromFirestore, setLoadingFromFirestore] = useState(false); // Added state

  const {
    mutate: generateSharePdfUrlMutation,
    isLoading: isGeneratingPdfUrl,
    error: pdfUrlGenerationError,
  } = useGetSharePdfUrl();
  const {
    data: tribeGroups = [],
    isLoading: tribeGroupsLoading,
    error: tribeGroupsError,
  } = useGetTribeGroups();
  const { mutate: createMultiGroupPost, isPending: isPostingToTribe } =
    useCreateMultiGroupPost();
  const {
    data: journalDetail,
    isLoading: journalDetailLoading,
    error: journalDetailError,
  } = useGetJournalDetail(journalId);
  const { user } = useUserStore();

  const filteredTribeGroups = filterTribeGroupByAccessLevel(
    tribeGroups,
    user?.id,
  );

  // Initialize conversations lists
  useEffect(() => {
    if (!show || !user?.id) return;

    setLoadingFromFirestore(true); // Start loading
    const q = query(
      collection(db, FIREBASE_CONVERSATIONS),
      where("participants", "array-contains", String(user.id)),
      orderBy("lastMessageTime", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const userConversations = [];
        querySnapshot.forEach((doc) => {
          const conversationData = doc.data();
          const conversationId = doc.id;

          // Ensure it's visible
          if (!conversationData.visibleTo?.includes(String(user.id))) {
            return;
          }

          if (conversationData.type === "direct") {
            // For direct chats, find the other participant
            const otherParticipantId = conversationData.participants.find(
              (participantId) => participantId !== String(user.id),
            );

            if (
              otherParticipantId &&
              conversationData.participantDetails &&
              conversationData.participantDetails[otherParticipantId]
            ) {
              const otherUser =
                conversationData.participantDetails[otherParticipantId];
              userConversations.push({
                conversationId: conversationId,
                id: conversationId, // Keep 'id' for compatibility with old logic
                name: otherUser.name,
                email: otherUser.email,
                profileImage: otherUser.profileImage,
                type: "direct",
                displayText: `${otherUser.name}`,
                ...conversationData, // Spread the rest of the data
              });
            }
          } else if (conversationData.type === "group") {
            // For group chats, use group name or create from participants
            const groupName = `${conversationData?.metadata?.groupName} (${conversationData.participants.length} members)`;

            userConversations.push({
              conversationId: conversationId,
              id: conversationId, // Keep 'id' for compatibility
              name: groupName,
              memberCount: conversationData.participants.length,
              type: "group",
              displayText: `${groupName}`,
              ...conversationData, // Spread the rest of the data
            });
          }
        });
        setConversations(userConversations);
        setLoadingFromFirestore(false); // Stop loading
      },
      (error) => {
        console.error("Error fetching conversations: ", error);
        setLoadingFromFirestore(false); // Stop loading on error
      },
    );

    return () => unsubscribe();
  }, [user?.id]);

  // Fetch All Users
  useEffect(() => {
    if (!show || !user?.id) return;

    const fetchAllUsers = async () => {
      setLoadingAllUsers(true);
      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef);
        const snapshot = await getDocs(q);

        const users = [];
        snapshot.forEach((doc) => {
          if (String(doc.id) !== String(user.id)) {
            users.push({ id: doc.id, ...doc.data() });
          }
        });

        setAllUsers(users);
      } catch (err) {
        console.error("Error fetching all users:", err);
        setAllUsers([]);
      }
      setLoadingAllUsers(false);
    };

    fetchAllUsers();
  }, [show, user?.id]);

  // Close custom dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatDropdownRef.current &&
        !chatDropdownRef.current.contains(event.target)
      ) {
        setIsChatDropdownOpen(false);
      }
      if (
        tribeGroupDropdownRef.current &&
        !tribeGroupDropdownRef.current.contains(event.target)
      ) {
        setIsTribeGroupDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSharePdfUrl("");
    setUrlGenerationError("");
  }, [selectedTabs]);

  const handleChatSelection = (conversationId) => {
    const isSelected = selectedChats.includes(conversationId);
    if (isSelected) {
      setSelectedChats((prev) => prev.filter((id) => id !== conversationId));
    } else {
      setSelectedChats((prev) => [...prev, conversationId]);
    }
  };

  const getSelectedDisplayText = () => {
    if (selectedChats.length === 0) {
      return loadingFromFirestore ? "Loading chats..." : "Select Chats";
    }
    if (selectedChats.length === 1) {
      const selected = conversations.find((c) => c.id === selectedChats[0]);
      return selected ? selected.displayText : "Unknown";
    }
    return `${selectedChats.length} chats selected`;
  };

  const handleTribeGroupSelection = (groupId) => {
    const idStr = String(groupId);
    const isSelected = selectedTribeGroups.includes(idStr);
    if (isSelected) {
      setSelectedTribeGroups((prev) => prev.filter((id) => id !== idStr));
    } else {
      setSelectedTribeGroups((prev) => [...prev, idStr]);
    }
  };

  const getSelectedTribeGroupsDisplayText = () => {
    if (selectedTribeGroups.length === 0)
      return tribeGroupsLoading ? "Loading groups..." : "Select Tribe Units";
    if (selectedTribeGroups.length === 1) {
      const selected = filteredTribeGroups.find(
        (g) => String(g.id) === selectedTribeGroups[0],
      );
      return selected ? selected.name || selected.group_name : "Unknown";
    }
    return `${selectedTribeGroups.length} groups selected`;
  };

  const searchUsers = async (term) => {
    if (!term || term.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchingUsers(true);

    try {
      const searchTerm = term.toLowerCase().trim();

      // Filter all users client-side (case-insensitive)
      const results = allUsers
        .filter((u) => u.name?.toLowerCase().includes(searchTerm))
        .slice(0, 10);

      setSearchResults(results);
    } catch (err) {
      console.error("Error searching users:", err);
      setSearchResults([]);
    }

    setSearchingUsers(false);
  };

  const ensureDirectConversationWith = async (otherUser) => {
    try {
      const userId = String(user.id);
      const otherUserId = String(otherUser.id);
      const sortedIds = [userId, otherUserId].sort(
        (a, b) => Number(a) - Number(b),
      );
      const conversationId = `${sortedIds[0]}_${sortedIds[1]}`;

      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);

      const participants = [userId, otherUserId];
      const participantDetails = {
        [userId]: {
          name: user.full_name || user.name || "",
          email: user.email || "",
          profileImage: user.image || "",
        },
        [otherUserId]: {
          name: otherUser.name || otherUser.full_name || "",
          email: otherUser.email || "",
          profileImage: otherUser.profileImage || otherUser.image || "",
        },
      };

      if (!convoSnap.exists()) {
        const newConvoData = {
          conversationId,
          participants,
          participantDetails,
          lastMessage: "",
          lastMessageSenderId: null,
          lastMessageTime: serverTimestamp(),
          unreadCount: { [userId]: 0, [otherUserId]: 0 },
          type: "direct",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          visibleTo: participants,
        };
        await setDoc(convoRef, newConvoData);

        // add to local conversations array for UI
        setConversations((prev) => [
          {
            id: conversationId,
            conversationId: conversationId,
            ...newConvoData,
            displayText: participantDetails[otherUserId].name,
          },
          ...prev,
        ]);
      } else {
        // If it exists, check if it's in our local state
        const existsInLocal = conversations.find(
          (c) => c.id === conversationId,
        );
        if (!existsInLocal) {
          // Add it, as it might have been created by the other user
          const existingData = convoSnap.data();
          setConversations((prev) => [
            {
              id: conversationId,
              conversationId: conversationId,
              ...existingData,
              displayText: existingData.participantDetails[otherUserId].name,
            },
            ...prev,
          ]);
        }
      }

      return conversationId;
    } catch (err) {
      console.error("Error ensuring conversation:", err);
      return null;
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery && searchQuery.trim().length >= 2) {
        searchUsers(searchQuery.trim());
      } else {
        setSearchResults([]);
      }
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleTabToggle = (tabKey) => {
    setSelectedTabs((prev) => {
      if (prev.includes(tabKey)) {
        if (prev.length === 1) {
          toast.error("Please select at least one section to share.");
          return prev;
        }
        // Remove tab if already selected
        return prev.filter((tab) => tab !== tabKey);
      } else {
        // Add tab if not selected
        return [...prev, tabKey];
      }
    });
  };

  const getJournalTitleAnswer = () => {
    const matchingAnswer = journalDetail?.user_journal?.journal_answers?.find(
      (answer) => answer.main_question?.keywords === "title",
    );

    return matchingAnswer?.text || "";
  };

  const handleGenerateShareUrl = () => {
    if (!journalId) {
      setUrlGenerationError("Journal ID is required to generate share URL");
      return;
    }

    if (selectedTabs.length === 0) {
      setUrlGenerationError("Please select at least one tab to share");
      return;
    }

    setUrlGenerationError("");

    const payload = {
      user_journal_id: journalId,
      journal_keywords: selectedTabs,
    };

    generateSharePdfUrlMutation(payload, {
      onSuccess: (response) => {
        if (response?.share_data?.share_url) {
          setSharePdfUrl(response.share_data.share_url);
          toast.success("Share URL generated successfully!");
        } else {
          console.error("Share URL not found in response:", response);
          setUrlGenerationError(
            "Failed to generate share URL - URL not found in response",
          );
        }
      },
      onError: (error) => {
        console.error("Generate URL Error:", error);
        const errorMessage =
          error?.response?.data?.message || "Failed to generate share URL";
        setUrlGenerationError(errorMessage);
        toast.error(errorMessage);
      },
    });
  };

  // --- NEW FUNCTION ---
  const handleCopyToClipboard = () => {
    if (!sharePdfUrl) return;

    navigator.clipboard
      .writeText(sharePdfUrl)
      .then(() => {
        // toast.success("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
        toast.error("Failed to copy link.");
      });
  };
  // --- END NEW FUNCTION ---

  const handleShare = async () => {
    const trimmedContent = messageContent.replace(/<[^>]*>/g, "").trim();

    if (selectedTabs.length === 0) {
      toast.error("Please select at least one section to share.");
      return;
    }

    if (selectedChats.length === 0 && selectedTribeGroups.length === 0) {
      toast.error("Please select at least one chat or tribe group.");
      return;
    }

    if (!trimmedContent) {
      toast.error("Please write a description message.");
      return;
    }

    setIsSharing(true);

    // Fire-and-forget parallel tasks with their own completion toasts
    // Chats task
    const chatShareTask = (async () => {
      if (selectedChats.length === 0) return;
      const results = await Promise.allSettled(
        selectedChats.map((chatId) =>
          handleSendJournalMessageInChat(chatId, messageContent, title),
        ),
      );
      const chatSuccess = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const chatFail = results.length - chatSuccess;
      if (chatSuccess > 0) {
        toast.success(
          `Shared to ${chatSuccess} chat${chatSuccess > 1 ? "s" : ""}.`,
        );
      }
      if (chatFail > 0) {
        toast.error(
          `Failed to share in ${chatFail} chat${chatFail > 1 ? "s" : ""}.`,
        );
      }
    })();

    // Tribe task
    const tribeShareTask = (async () => {
      if (selectedTribeGroups.length === 0) return;
      try {
        await handleSendJournalInTribeGroups(selectedTribeGroups);
        toast.success(
          `Shared to ${selectedTribeGroups.length} tribe group${selectedTribeGroups.length > 1 ? "s" : ""}.`,
        );
      } catch (err) {
        console.error("Tribe share failed:", err);
        toast.error("Failed to share to tribe groups.");
      }
    })();

    // Let them run in background (no await), and turn off the header progress once both finish
    Promise.allSettled([chatShareTask, tribeShareTask])
      .then(() => {
        setIsSharing(false);
        onClose();
      })
      .catch(() => {
        setIsSharing(false);
        onClose();
      });
  };

  const handleSendJournalMessageInChat = async (
    conversationId,
    content,
    title,
  ) => {
    try {
      const convoRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
      const convoSnap = await getDoc(convoRef);

      if (!convoSnap.exists()) {
        console.error("Conversation not found");
        throw new Error("Conversation not found");
      }
      const convoData = convoSnap.data();
      const visibleToUserIds = convoData.participants || [];

      const messageData = {
        type: "text",
        metadata: {
          title: title,
        },
        reactions: [],
        isRead: null,
        senderId: user.id,
        senderName: user.full_name,
        senderProfileImage: user.image,
        timestamp: serverTimestamp(),
        content: content,
        visibleTo: visibleToUserIds,

        journal: {
          isJournalShared: true,
          journal_keywords: selectedTabs,
          user_journal_id: journalId,
          emotion_name: title,
          title_answer: getJournalTitleAnswer(),
        },
      };

      await addDoc(
        collection(db, FIREBASE_CONVERSATIONS, conversationId, "messages"),
        messageData,
      );

      await runTransaction(db, async (transaction) => {
        const convoSnap = await transaction.get(convoRef);
        if (!convoSnap.exists()) return;

        const convoData = convoSnap.data();
        const currentUnread = convoData.unreadCount || {};
        // Use only participants (not visibleTo) to avoid re-adding removed members
        const allParticipants = (convoData.participants || []).filter(Boolean);

        const updatedUnread = { ...currentUnread };

        // Ensure every participant is in the map and increment if not sender
        allParticipants.forEach((uid) => {
          const uidStr = String(uid);
          if (!(uidStr in updatedUnread)) {
            updatedUnread[uidStr] = 0;
          }

          if (uidStr !== String(user.id)) {
            updatedUnread[uidStr] = (updatedUnread[uidStr] || 0) + 1;
          }
        });

        // Re-derive unreadUserIds: Missing entries here prevent push notifications
        const unreadUserIds = Object.keys(updatedUnread).filter(
          (uid) => updatedUnread[uid] > 0,
        );

        transaction.update(convoRef, {
          lastMessage: content || `Shared journal: ${title}`,
          lastMessageSenderId: user.id,
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          unreadCount: updatedUnread,
          unreadUserIds,
          participants: allParticipants, // Fix the participants list
        });
      });
    } catch (error) {
      console.error("Failed to send journal message:", error);
      throw error; // Propagate the error to the calling function
    }
  };

  const handleSendJournalInTribeGroups = async (groupIds = []) => {
    if (groupIds.length === 0) return;

    const formData = new FormData();
    // Append selected group IDs
    groupIds.forEach((id) => {
      formData.append("group_ids[]", id);
    });

    // Include share link if available under the message text
    const finalText = sharePdfUrl
      ? `${messageContent}\n\n${sharePdfUrl}`
      : messageContent;
    formData.append("text", finalText || "");

    // Journal fields expected by API
    formData.append("is_journal_shared", 1);
    formData.append("user_journal_id", String(journalId));
    formData.append("title_answer", getJournalTitleAnswer());
    selectedTabs.forEach((tab) =>
      formData.append("shared_journal_keywords[]", tab),
    );

    // Wrap mutate callback in a Promise so we can await completion
    return new Promise((resolve, reject) => {
      createMultiGroupPost(formData, {
        onSuccess: (response) => {
          resolve(response);
        },
        onError: (error) => {
          console.error("❌ Error sharing to tribe groups:", error);
          reject(error);
        },
      });
    });
  };

  if (!show) return null;

  return (
    <>
      {/* Custom Modal Overlay */}
      <div
        className="custom-modal-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1050,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={onClose}
      >
        <div
          className="custom-modal-content habit-modal-content"
          style={{
            backgroundColor: "#132534",
            borderRadius: "8px",
            maxWidth: "600px",
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            position: "relative",
            margin: "20px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div
            className="modal-header"
            style={{
              padding: "1rem 1.5rem",
              borderBottom: "1px solid #364756",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            <h5
              className="modal-title"
              id="shareScoreModalLabel"
              style={{ margin: 0, color: "#e6ecf1", textAlign: "center" }}
            >
              {title}
            </h5>
            <button
              type="button"
              className="custom-close-btn"
              aria-label="Close"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                color: "#e6ecf1",
                cursor: "pointer",
                padding: "0",
                lineHeight: "1",
                position: "absolute",
                right: "1.5rem",
              }}
            >
              ×
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body" style={{ padding: "1.5rem" }}>
            {/* Tab Switcher */}
            <div
              style={{
                backgroundColor: "#1B2C3A",
                borderRadius: "25px",
                padding: "8px",
                display: "flex",
                gap: "12px",
                marginBottom: "1.5rem",
                justifyContent: "center",
              }}
            >
              {[
                { key: "title", label: "Title" },
                { key: "lesson", label: "Lesson" },
                { key: "summary", label: "Summary" },
                { key: "full", label: "Full" },
              ].map((tab) => {
                const isActive = selectedTabs.includes(tab.key);
                return (
                  <button
                    key={tab.key}
                    onClick={() => handleTabToggle(tab.key)}
                    style={{
                      backgroundColor: isActive ? "#008CFF" : "#131F28",
                      color: isActive ? "white" : "#e6ecf1",
                      border: isActive ? "none" : "1px solid #008CFF",
                      borderRadius: "25px",
                      height: "36px",
                      width: "140px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="mb-4">
              <div className="notes-text-editor">
                <label
                  className="text-small-s2"
                  style={{
                    color: "#D0D8DF",
                    fontSize: "10px",
                    fontWeight: "400",
                  }}
                >
                  Description
                </label>
                <MyEditor
                  value={messageContent}
                  onChange={setMessageContent}
                  readOnly={isSharing}
                />
              </div>
            </div>

            {/* Tribe Groups Selection */}
            <div className="mb-3">
              <div
                className="chat-container"
                style={{ padding: "0px", height: "auto", overflowY: "unset" }}
              >
                <div
                  className="textfield-1 position-relative"
                  ref={tribeGroupDropdownRef}
                  style={{ borderColor: "#364756" }}
                >
                  <input
                    type="text"
                    className="custom-select text-body-b2"
                    placeholder=""
                    value={getSelectedTribeGroupsDisplayText()}
                    onClick={() =>
                      setIsTribeGroupDropdownOpen(!isTribeGroupDropdownOpen)
                    }
                    readOnly
                    disabled={tribeGroupsLoading}
                    style={{
                      opacity: tribeGroupsLoading ? 0.6 : 1,
                      borderColor: "#364756",
                    }}
                  />
                  {isTribeGroupDropdownOpen && (
                    <div
                      className="custom-select-dropdown"
                      style={{
                        zIndex: 10,
                        display: "block",
                        position: "absolute",
                        width: "100%",
                        background: "#192f41",
                        border: "1px solid #2d3c48",
                        borderRadius: "20px",
                        padding: "16px 20px",
                        boxShadow: "2px 4px 6px 0px #0000002e",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      <h5
                        className="text-body-b2 mb-2"
                        style={{ color: "#E6ECF1", fontWeight: "500" }}
                      >
                        Tribe Groups
                      </h5>
                      {filteredTribeGroups.map((group) => (
                        <div
                          key={group.id}
                          className="d-flex align-items-center mb-2"
                          style={{ cursor: "pointer", gap: "12px" }}
                          onClick={() => handleTribeGroupSelection(group.id)}
                        >
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              border: selectedTribeGroups.includes(
                                String(group.id),
                              )
                                ? "2px solid #00B4FF"
                                : "2px solid #2d3c48",
                              borderRadius: "4px",
                              backgroundColor: selectedTribeGroups.includes(
                                String(group.id),
                              )
                                ? "#00B4FF"
                                : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                              flexShrink: 0,
                            }}
                          >
                            {selectedTribeGroups.includes(String(group.id)) && (
                              <svg
                                width="12"
                                height="9"
                                viewBox="0 0 12 9"
                                fill="none"
                                style={{ color: "white" }}
                              >
                                <path
                                  d="M1 4.5L4.5 8L11 1.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="body-1-default label-san-target text-truncate-ellipsis">
                            {group.name || group.group_name}
                          </span>
                        </div>
                      ))}

                      {filteredTribeGroups.length === 0 &&
                        !tribeGroupsLoading && (
                          <span className="text-body-b2 text-muted">
                            No tribe groups found
                          </span>
                        )}
                    </div>
                  )}
                  <label
                    className="text-body-b2"
                    style={{ backgroundColor: "#132534" }}
                  >
                    Tribe Groups
                  </label>
                </div>
              </div>
            </div>

            {/* START: New Chat Selection */}
            <div style={{ marginBottom: "24px" }}>
              <div
                className="textfield-1 position-relative"
                ref={chatDropdownRef}
              >
                <input
                  type="text"
                  className="custom-select text-body-b2"
                  placeholder=""
                  value={getSelectedDisplayText()}
                  onClick={() => setIsChatDropdownOpen(!isChatDropdownOpen)}
                  readOnly
                  disabled={loadingFromFirestore}
                  style={{
                    opacity: loadingFromFirestore ? 0.6 : 1,
                    borderColor: "#364756",
                  }}
                />
                {isChatDropdownOpen && (
                  <div
                    className="custom-select-dropdown"
                    style={{
                      zIndex: 10,
                      display: "block",
                      position: "absolute",
                      width: "100%",
                      background: "#192f41",
                      border: "1px solid #2d3c48",
                      borderRadius: "20px",
                      padding: "16px 20px",
                      boxShadow: "2px 4px 6px 0px #0000002e",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {/* Search input to look up users (queries Firestore on demand) */}
                    <div style={{ marginBottom: "12px" }}>
                      <input
                        type="text"
                        placeholder="Search people to share... (type 2+ chars)"
                        className="text-body-b2"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          background: "#0f2734",
                          color: "#e6ecf1",
                          border: "1px solid #23323d",
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            searchUsers(searchQuery);
                          }
                        }}
                      />
                    </div>

                    {/* If search query present, show live search results first */}
                    {searchQuery && searchQuery.trim().length >= 2 ? (
                      <div>
                        {searchingUsers ? (
                          <div className="text-body-b2 text-white">
                            Searching...
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map((u) => (
                            <div
                              key={u.id}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={async () => {
                                // ensure conversation exists, then select it
                                const convoId =
                                  await ensureDirectConversationWith(u);
                                if (convoId) {
                                  // select it
                                  if (!selectedChats.includes(convoId)) {
                                    setSelectedChats((prev) => [
                                      ...prev,
                                      convoId,
                                    ]);
                                  }
                                  setSearchQuery("");
                                  setSearchResults([]);
                                  // setIsChatDropdownOpen(false); // Keep it open to select more
                                }
                              }}
                            >
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: selectedChats.includes(u.id) // This might be slightly off if convoId is different, but OK for UI
                                    ? "2px solid #00B4FF"
                                    : "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  backgroundColor: selectedChats.includes(u.id)
                                    ? "#00B4FF"
                                    : "transparent",
                                  flexShrink: 0,
                                }}
                              />
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {u.name}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="text-body-b2 text-white">
                            No users found
                          </div>
                        )}
                        <hr
                          style={{ margin: "12px 0", borderColor: "#23323d" }}
                        />
                      </div>
                    ) : null}

                    {/* Individual Chats Section */}
                    {conversations.filter((conv) => conv.type === "direct")
                      .length > 0 && (
                      <>
                        <h5
                          className="text-body-b2 mb-2"
                          style={{ color: "#E6ECF1", fontWeight: "500" }}
                        >
                          Individual Chat
                        </h5>
                        {conversations
                          .filter((conv) => conv.type === "direct")
                          .map((conversation) => (
                            <div
                              key={conversation.conversationId}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={() =>
                                handleChatSelection(conversation.conversationId)
                              }
                            >
                              {/* Custom Square Checkbox */}
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "2px solid #00B4FF"
                                    : "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  backgroundColor: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "#00B4FF"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  flexShrink: 0,
                                }}
                              >
                                {selectedChats.includes(
                                  conversation.conversationId,
                                ) && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    style={{ color: "white" }}
                                  >
                                    <path
                                      d="M1 4.5L4.5 8L11 1.5"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {conversation.displayText}
                              </span>
                            </div>
                          ))}
                      </>
                    )}

                    {/* Separator Line */}
                    {conversations.filter((conv) => conv.type === "direct")
                      .length > 0 &&
                      conversations.filter((conv) => conv.type === "group")
                        .length > 0 && (
                        <hr
                          style={{ margin: "16px 0", borderColor: "#2d3c48" }}
                        />
                      )}

                    {/* Group Chats Section */}
                    {conversations.filter((conv) => conv.type === "group")
                      .length > 0 && (
                      <>
                        <h5
                          className="text-body-b2 mb-2"
                          style={{ color: "#E6ECF1", fontWeight: "500" }}
                        >
                          Group Chat
                        </h5>
                        {conversations
                          .filter((conv) => conv.type === "group")
                          .map((conversation) => (
                            <div
                              key={conversation.conversationId}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: "pointer", gap: "12px" }}
                              onClick={() =>
                                handleChatSelection(conversation.conversationId)
                              }
                            >
                              {/* Custom Square Checkbox */}
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "2px solid #00B4FF"
                                    : "2px solid #2d3c48",
                                  borderRadius: "4px",
                                  backgroundColor: selectedChats.includes(
                                    conversation.conversationId,
                                  )
                                    ? "#00B4FF"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  flexShrink: 0,
                                }}
                              >
                                {selectedChats.includes(
                                  conversation.conversationId,
                                ) && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    style={{ color: "white" }}
                                  >
                                    <path
                                      d="M1 4.5L4.5 8L11 1.5"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="body-1-default label-san-target text-truncate-ellipsis">
                                {conversation.displayText}
                              </span>
                            </div>
                          ))}
                      </>
                    )}

                    {conversations.length === 0 && !loadingFromFirestore && (
                      <span className="text-body-b2 text-muted">
                        No conversations found
                      </span>
                    )}
                  </div>
                )}
                <label
                  className="text-body-b2"
                  style={{ background: "#132534" }}
                >
                  Chat
                </label>
              </div>
            </div>
            {/* END: New Chat Selection */}

            {/* --- MODIFIED SHARE LINK SECTION --- */}
            <div className="mb-3">
              <label
                className="text-small-s2"
                style={{
                  color: "#D0D8DF",
                  fontSize: "10px",
                  fontWeight: "400",
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Share Link
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#131F28",
                  border: "1px solid #364756",
                  borderRadius: "50px", // Fully pill-shaped
                  paddingLeft: "16px",
                  paddingRight: "4px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                }}
              >
                <input
                  type="text"
                  value={sharePdfUrl}
                  readOnly
                  placeholder="Generated share link will appear here..."
                  style={{
                    flex: 1,
                    background: "none",
                    border: "none",
                    outline: "none",
                    color: "#e6ecf1",
                    fontSize: "14px",
                    padding: "8px 0", // Adjusted vertical padding
                  }}
                />
                <button
                  onClick={
                    sharePdfUrl ? handleCopyToClipboard : handleGenerateShareUrl
                  }
                  disabled={
                    sharePdfUrl
                      ? false
                      : isGeneratingPdfUrl || selectedTabs.length === 0
                  }
                  style={{
                    backgroundColor: "#008CFF",
                    color: "white",
                    border: "none",
                    borderRadius: "50px", // Pill shape
                    padding: "10px 24px", // Slightly less vertical padding
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: (
                      sharePdfUrl
                        ? false
                        : isGeneratingPdfUrl || selectedTabs.length === 0
                    )
                      ? "not-allowed"
                      : "pointer",
                    opacity: (
                      sharePdfUrl
                        ? false
                        : isGeneratingPdfUrl || selectedTabs.length === 0
                    )
                      ? 0.6
                      : 1,
                    whiteSpace: "nowrap",
                    flexShrink: 0, // Prevent button from shrinking
                  }}
                >
                  {sharePdfUrl
                    ? "Copy Link"
                    : isGeneratingPdfUrl
                      ? "Generating..."
                      : "Generate"}
                </button>
              </div>
              {urlGenerationError && (
                <div
                  style={{
                    color: "#ff4757",
                    fontSize: "12px",
                    marginTop: "8px",
                  }}
                >
                  {urlGenerationError}
                </div>
              )}
            </div>
            {/* --- END MODIFIED SECTION --- */}

            <button
              type="button"
              className="btn-2 btn-1-primary habit-share-btn"
              onClick={handleShare}
              disabled={
                isSharing ||
                (selectedChats.length === 0 &&
                  selectedTribeGroups.length === 0) ||
                !messageContent.replace(/<[^>]*>/g, "").trim()
              }
              style={{
                // width: '100%',
                opacity:
                  isSharing ||
                  (selectedChats.length === 0 &&
                    selectedTribeGroups.length === 0) ||
                  !messageContent.replace(/<[^>]*>/g, "").trim()
                    ? 0.6
                    : 1,
                cursor:
                  isSharing ||
                  (selectedChats.length === 0 &&
                    selectedTribeGroups.length === 0) ||
                  !messageContent.replace(/<[^>]*>/g, "").trim()
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isSharing ? (
                <>
                  <span className="ms-2">Sharing...</span>
                </>
              ) : (
                "Share"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default JournalShareModal;
